import React from 'react';
import { motion } from 'framer-motion';
import { 
  BrainCircuit, 
  Cpu, 
  Fingerprint, 
  Zap, 
  Activity, 
  Network
} from 'lucide-react';
import './SafePulseFeaturesSection.css';

const SafePulseFeaturesSection = () => {
  return (
    <section className="sp-tech-section">
      <div className="sp-tech-container">
        <div className="sp-tech-header">
          <motion.div 
            className="sp-tech-badge"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Core Technology
          </motion.div>
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="sp-tech-title"
          >
            Adaptive Intelligence
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="sp-tech-subtitle"
          >
            Security that lives and breathes. A self-learning neural engine that evolves with your working style.
          </motion.p>
        </div>

        <div className="sp-bento-grid">
          {/* Main Large Card - Continuous Evolution */}
          <motion.div 
            className="sp-bento-card evolution-card"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="card-bg-gradient"></div>
            <div className="card-content-wrapper">
              <div className="icon-badge blue-badge">
                <BrainCircuit size={24} />
              </div>
              <h3>Continuous Evolution</h3>
              <p>Static biometrics fail. SafePulse builds a living profile that updates with every keystroke, adapting to your natural changes in rhythm, speed, and fatigue levels.</p>
              <div className="tech-visual-row">
                <div className="tech-stat">
                  <span className="tech-stat-val">Live</span>
                  <span className="tech-stat-label">Model Updates</span>
                </div>
                <div className="tech-divider"></div>
                <div className="tech-stat">
                  <span className="tech-stat-val">0%</span>
                  <span className="tech-stat-label">Decay Rate</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Tall Card - Micro-Signals */}
          <motion.div 
            className="sp-bento-card signals-card"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="card-content-wrapper">
              <div className="icon-badge purple-badge">
                <Fingerprint size={24} />
              </div>
              <h3>2,000+ Micro-Signals</h3>
              <p>Analyzing thousands of data points per minute. From key-flight time and dwell duration to cursor micro-jitter and angular velocity.</p>
              
              <div className="signal-visual">
                <div className="signal-line"></div>
                <div className="signal-line"></div>
                <div className="signal-line"></div>
                <div className="signal-dot"></div>
              </div>
            </div>
          </motion.div>

          {/* Medium Card - Edge Processing */}
          <motion.div 
            className="sp-bento-card edge-card"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="icon-badge orange-badge">
              <Cpu size={24} />
            </div>
            <h3>Edge-Native</h3>
            <p>All processing happens on-chip. No cloud latency.</p>
            <div className="mini-tag">{'<'} 10ms Latency</div>
          </motion.div>

          {/* Medium Card - Flow State */}
          <motion.div 
            className="sp-bento-card flow-card"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div className="icon-badge green-badge">
              <Zap size={24} />
            </div>
            <h3>Flow State</h3>
            <p>Security that never interrupts your deep work.</p>
            <div className="mini-tag">100% Passive</div>
          </motion.div>

          {/* Wide Card - Cognitive Fingerprint */}
          <motion.div 
            className="sp-bento-card cognitive-card"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <div className="card-row-layout">
              <div className="card-text-side">
                <div className="icon-badge gray-badge">
                  <Activity size={24} />
                </div>
                <h3>Cognitive Fingerprint</h3>
                <p>Maps the unique way your brain processes information and translates it into physical action. Impossible for bots or imposters to mimic.</p>
              </div>
              <div className="card-visual-side">
                <Network className="network-icon" size={64} strokeWidth={1} />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default SafePulseFeaturesSection;
